import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { DataContext } from ".";
import useMessage, { MESSAGE_TYPES } from "../../hooks/useMessage";
import useResource from "../../hooks/useResource";

import inPreview from "../../utils/inPreview";
import getRemoteOptions from "../../utils/getRemoteOptions";
import { Loader } from "../CoreUI";
import {
	WIDGET_VERSION,
	FETCH_WIDGET_URL,
	CURR_PREVIEW_TYPE,
	PREVIEW_TYPE_CONFIG,
	PUBLISHER,
	WIDGET_TYPE,
	MODES,
} from "../../conf";
import message from "../../utils/message";

/*
	### Central store for all widget data ###
		Gets data from script messages IF not running in platform mode
		Fetches data from the backend IF running in platform mode
*/

// Marlowe's Roastery: 32811b18-4a9f-4003-9899-8eb2e2c0c0a1

/*
data's shape:
	{
		surfaceType: MODULE or WIDGET,
		publisher: publisher's remote_id,
		env: staging or development or production
		...rest widget's data (probably different from push/ response as we now need tabbed categories)
	}
*/

const MAX_LOAD_MORE_TRIES = 3;

// TODO: QA DataManager & re-check diff when working with lead gen
export const procData = ({ data, isRemote = false }) => {
	const { l = [] } = data || {};

	let procData = {
		...data,
	};

	procData = {
		...procData,
		l,
		feedHasUpsell:
			!!data?.self_products &&
			Array.isArray(data.self_products.products) &&
			data.self_products.products.length > 0,
		allBrands:
			!!data?.self_products &&
			Array.isArray(data.self_products.products) &&
			data.self_products.products.length > 0
				? [{ ...data.self_products, self: true }, ...l]
				: l,
		runningExperiments: procData?.runningExperiments || [],
	};

	if (isRemote) {
		procData = {
			...procData,
			scriptMetadata: {
				active_widget_type: procData.widget_type,
				active_widget_version: procData.widget_version,
			},
		};
	}

	const {
		active_widget_type: activeWidgetType = WIDGET_TYPE.DISCOFEED, // use DiscoFeed as the fallback version (will kick in for platform previews)
		active_widget_version: activeWidgetVersion = WIDGET_VERSION[
			WIDGET_TYPE.DISCOFEED
		][2.1], // use DiscoFeed as the fallback version (will kick in for platform previews)
	} = procData?.scriptMetadata || {};

	switch (activeWidgetType) {
		case WIDGET_TYPE.LEAD_GEN: {
			const generateBrandImageKeys = (brands) => {
				if (!brands || !Array.isArray(brands)) return brands;
				return brands.map((brand) => {
					const parsedBannerUrlKey =
						brand?.banner_url?.match(
							/https?:\/\/[^/]+\/(.+)/
						)?.[1] || null;

					return {
						...brand,
						banner_key: parsedBannerUrlKey,
					};
				});
			};
			procData = {
				...procData,
				brands: generateBrandImageKeys(procData.brands),
			};
			break;
		}
		case WIDGET_TYPE.DISCOFEED: {
			switch (activeWidgetVersion) {
				case WIDGET_VERSION[WIDGET_TYPE.DISCOFEED][2.0]:
				case WIDGET_VERSION[WIDGET_TYPE.DISCOFEED][2.1]: {
					const listThresh = 3;
					const list = l.slice(0, listThresh);
					const listBackup = l.slice(listThresh);

					procData = {
						...procData,
						l: list,
						listBackup,
					};
				}
			}
		}
	}
	return procData;
};

const DataManager = memo(({ children, mock = false }) => {
	const [{ loading, error, data: remoteData }, load, reset] = useResource(
		getRemoteOptions(),
		false
	);

	const [data, setData] = useState(null);

	const [loadMoreTries, setLoadMoreTries] = useState(0);
	const [canLoadMore, setCanLoadMore] = useState(true);

	// keep this here as tracker manager does not get rendered until data gets populated
	const [canTrack, setCanTrack] = useState(false);
	const handleTrackingMessage = useCallback(() => setCanTrack(true), []);
	useMessage(MESSAGE_TYPES.START_TRACK, handleTrackingMessage);

	const handleLoadMessage = useCallback((payload) => {
		if (inPreview) {
			return;
		}

		setData(procData({ data: payload, isRemote: false }));
	}, []);

	useMessage(MESSAGE_TYPES.LOAD, handleLoadMessage);

	useEffect(() => {
		if (!inPreview || data || remoteData || error || loading) return;
		load();
	}, [load, error, data, loading, remoteData]);

	useEffect(() => {
		message(MESSAGE_TYPES.AVAILABLE);
	}, []);

	useEffect(() => {
		if (!remoteData) return;

		setData({
			...procData({ data: remoteData, isRemote: true }),
			publisher: PUBLISHER,
			view: PREVIEW_TYPE_CONFIG[CURR_PREVIEW_TYPE]?.view,
			initialMode: MODES.INLINE,
			innerHeight: window.innerHeight,
			innerWidth: window.innerWidth,
			parentInnerHeight: window.innerHeight,
			parentInnerWidth: window.innerWidth,
			loadMoreUrl: FETCH_WIDGET_URL,
			scrapedInformation: {
				first_name: "Marlowe",
				identifier: "Order_ID_Coop_Test",
				email: "test@disconetwork.com",
			},
		});
		reset();
	}, [reset, remoteData]);

	const groupRefs = useRef([]);
	const containerRef = useRef(null);

	const [
		{ loading: moreLoading, error: moreError, data: moreData },
		loadMoreCall,
		resetMore,
	] = useResource(
		{
			url: data?.loadMoreUrl,
			data: {
				session_id: data?.session_id,
				recommendation_id: data?.recommendation_id,
				...(inPreview ? { platform: true } : {}),
			},
			method: "POST",
		},
		false
	);

	const moreLoadingRef = useRef(false);

	const [lastLoadCount, setLastLoadCount] = useState(-1);

	const loadMoreBrands = useCallback(() => {
		if (moreLoadingRef.current || moreLoading || lastLoadCount === 0)
			return;

		if (Array.isArray(data.listBackup) && data.listBackup.length > 0) {
			setData((data) => ({
				...data,
				l: [...data.l, ...data.listBackup],
				listBackup: null,
			}));
			return;
		}

		console.log("Loading more!");
		moreLoadingRef.current = true;
		loadMoreCall();
	}, [moreLoading, loadMoreCall, data, lastLoadCount]);

	useEffect(() => {
		if (!moreData && !moreError) {
			return;
		}
		let tries = loadMoreTries;
		if (moreError && tries < MAX_LOAD_MORE_TRIES) {
			setLoadMoreTries(tries + 1);
			tries++;
		} else if (moreData) {
			setData((data) => {
				if (
					!data ||
					!Array.isArray(data.l) ||
					!Array.isArray(moreData.l) ||
					moreData.l.length === 0
				) {
					if (!Array.isArray(moreData.l) || moreData.l.length === 0) {
						setCanLoadMore(false);
					}
					return data;
				}

				return {
					...data,
					l: [...data.l, ...moreData.l],
					incentives: {
						...data.incentives,
						...(moreData?.incentives || {}),
					},
				};
			});

			setLastLoadCount(moreData.l.length);
			setLoadMoreTries(0);
			tries = 0;
		}

		moreLoadingRef.current = false;
		if (tries < MAX_LOAD_MORE_TRIES) resetMore();
	}, [moreData, resetMore, moreError, loadMoreTries]);

	return (
		<DataContext.Provider
			value={{
				...data,
				setData,
				canTrack,
				groupRefs,
				containerRef,
				loadMoreBrands,
				moreLoading: moreLoading || moreLoadingRef.current,
				moreError,
				canLoadMore,
				lastLoadCount,
			}}
		>
			{data || mock ? (
				children
			) : (
				<Loader center data-testid="initial-data-loader" />
			)}
		</DataContext.Provider>
	);
});

export default DataManager;
